import {norm} from './numeric';
import {chalkContact} from './chalk';
// Planar rolling plus 3D flight and spin. Fixed dt=1/240, semi-implicit Euler
// for friction + swept (analytic TOI) ball-ball / cushion / jaw collisions.
// Mirror of backend/app/sim/physics.py — keep constants + behavior in sync.
import { BALL_R, POCKETS, TABLE_H, TABLE_W, cushions, jaws } from './table';

const CUSHIONS = cushions();
const JAWS = jaws();

export const DT = 1 / 240;
const G = 9.81;
const MU_S = 0.2; // sliding friction (Dr. Dave typical)
const MU_R = 0.01; // rolling resistance (tournament cloth)
const E_BALL = 0.94;
const E_CUSH_N = 0.76; // cushion COR along contact normal (nose 15.7° tilt)
const MU_CUSH = 0.17; // cushion tangential Coulomb friction
const SPIN_DECAY = 10; // rad/s^2, linear sidespin decay on cloth
const SLEEP_V = 0.005;
const SLEEP_W = 0.5;
const TIP_C = 2.5; // SRF = 2.5*(b/R): exact impulse model (TP A.12)
const TIP_MAX = 0.55; // miscue limit (fraction of R)
const SQUIRT_K = (5.7 * Math.PI) / 180; // aim deflect per unit side offset (normal cue)
export const VMAX_NORMAL = 3.5;
export const VMAX_BREAK = 8.5;
const VMIN = 0.55; // nothing dies short

export interface Ball {
  id: number; // 0..15 index
  n: number | null; // ball number 1..15, null = cue
  x: number; y: number; z: number; // bottom height above cloth (0 at rest)
  vx: number; vy: number; vz: number;
  wx: number; wy: number; wz: number;
  asleep: boolean;
  potted: boolean;
}

export interface ShotEvents {
  firstContact: number | null; // ball number (or -1 = cue? use id) first hit by cue
  potted: number[]; // ball numbers in order
  offTable: Array<number | null>; // driven off the table (null = cue)
  railAfterContact: boolean;
  cuePotted: boolean;
  pockets?: Array<{ n: number; pocket: number }>;
  firstContactX?: number;
  cueLeftKitchen?: boolean;
  objectRails?: number[];
}

export function makeBall(id: number, n: number | null, x: number, y: number): Ball {
  return { id, n, x, y, z: 0, vx: 0, vy: 0, vz: 0, wx: 0, wy: 0, wz: 0, asleep: true, potted: false };
}

/** Power [0,1] -> cue-ball speed m/s. Break rips, normal play stays calm. */
export function shootSpeed(power: number, vmax = VMAX_NORMAL): number {
  const p = Math.min(1, Math.max(0, power));
  // Match the server's launch-speed calibration to 1 picometre/second.
  // This removes pow's runtime ULP noise without rounding any ball state.
  const speed = VMIN + Math.pow(p, 1.55) * (vmax - VMIN);
  return Math.floor(speed * 1e12 + 0.5) / 1e12;
}

/** Throw friction falls with closing speed (Dr. Dave fit of Colenso data). */
export function throwMu(vRel: number): number {
  const mu = 0.016 + 0.219 * Math.exp(-0.691 * Math.abs(vRel));
  return Math.max(0.02, Math.min(0.235, mu));
}

/** Rigid cue impulse + frictional slate rebound. See pooltool's
 * instantaneous_point and frictional_inelastic ball/table models. */
export function strike(b: Ball, dx: number, dy: number, power: number, tipX: number, tipY: number, vmax = VMAX_NORMAL, elevation = 0, chalkLevel = 1): void {
  const offset = norm(tipX, tipY);
  const scale = offset > TIP_MAX ? TIP_MAX / offset : 1;
  const tx = tipX * scale, ty = tipY * scale;
  const theta = Math.max(0, Math.min(Math.PI / 2 - 0.01, elevation));
  const ct = Math.cos(theta), st = Math.sin(theta);
  const v = shootSpeed(power, vmax);
  const sq = tx * SQUIRT_K;
  const c = Math.cos(sq), s = Math.sin(sq);
  const rx = dx * c - dy * s, ry = dx * s + dy * c;
  b.vx = rx * v * ct; b.vy = ry * v * ct;
  b.vz = -v * st; // raised butt drives the ball DOWN into the slate
  const w = TIP_C * v / BALL_R;
  b.wx = w * (-tx * st * rx - ty * ry);
  b.wy = w * (-tx * st * ry + ty * rx);
  b.wz = -w * tx * ct;
  const {h,grip}=chalkContact(chalkLevel,tx,ty);
  if(grip<1){
    const forward=h*h+grip*(1-h*h),side=-h*tx*(1-grip),up=-h*ty*(1-grip);
    b.vx=v*(forward*rx*ct-side*ry+up*rx*st);
    b.vy=v*(forward*ry*ct+side*rx+up*ry*st);
    b.vz=v*(-forward*st+up*ct);
    b.wx*=grip;b.wy*=grip;b.wz*=grip;
  }
  b.asleep = false;
  if (b.z <= 1e-9 && b.vz < 0) land(b);
}

/** Normal restitution plus a Coulomb-limited tangential impulse. */
export function land(b: Ball): void {
  b.z = 0;
  if (b.vz >= 0) return;
  const normal = -1.5 * b.vz; // slate restitution 0.5 (tunable approximation)
  const ux = b.vx - BALL_R * b.wy, uy = b.vy + BALL_R * b.wx;
  const slip = norm(ux, uy);
  if (slip > 1e-12) {
    const impulse = Math.min(2 * slip / 7, MU_S * normal);
    const ix = -impulse * ux / slip, iy = -impulse * uy / slip;
    b.vx += ix; b.vy += iy;
    b.wx += 2.5 * iy / BALL_R; b.wy -= 2.5 * ix / BALL_R;
  }
  b.vz *= -0.5;
  if (b.vz * b.vz / (2 * G) < 0.0005) b.vz = 0;
}

function advance(b: Ball, dt: number, ev: ShotEvents, cueId: number): void {
  if (b.potted || b.asleep) return;
  b.x += b.vx * dt; b.y += b.vy * dt;
  if (b.z !== 0 || b.vz !== 0 || hole(b.x,b.y) !== null) {
    b.z += b.vz * dt - 0.5 * G * dt * dt;
    b.vz -= G * dt;
    if (b.z < 0 && b.z > -1e-7) b.z = 0;
  }
  if (b.id === cueId && ev.firstContact === null && b.x >= TABLE_W / 4) ev.cueLeftKitchen = true;
}

function friction(b: Ball, dt: number): void {
  const ux = b.vx - BALL_R * b.wy;
  const uy = b.vy + BALL_R * b.wx;
  const s = norm(ux, uy);
  // Solid-sphere contact slip decays at (1 + 5/2)*mu*g. Stop the
  // sliding impulse exactly at zero slip, then roll for the remaining time.
  const slideTime = Math.min(dt, s / (3.5 * MU_S * G));
  if (s > 1e-12) {
    const impulse = MU_S * G * slideTime;
    b.vx -= impulse * ux / s; b.vy -= impulse * uy / s;
    b.wx -= 2.5 * impulse * uy / (BALL_R * s);
    b.wy += 2.5 * impulse * ux / (BALL_R * s);
  }
  if (slideTime < dt) {
    const speed = norm(b.vx, b.vy);
    const deceleration = Math.min(speed, MU_R * G * (dt - slideTime));
    if (speed > 1e-12) { b.vx -= deceleration * b.vx / speed; b.vy -= deceleration * b.vy / speed; }
    b.wx = -b.vy / BALL_R; b.wy = b.vx / BALL_R;
  }
  // Sidespin decay.
  if (b.wz !== 0) {
    const d = Math.min(Math.abs(b.wz), SPIN_DECAY * dt);
    b.wz -= Math.sign(b.wz) * d;
  }
}

interface Contact { t: number; kind: 'bb' | 'rail' | 'jaw' | 'floor'; a: number; b: number; nx: number; ny: number; nz?: number }

function earliestContact(balls: Ball[], dt: number): Contact | null {
  let best: Contact | null = null;
  const R2 = BALL_R * 2;
  const live = balls.filter((b) => !b.potted);
  // Ball-ball swept TOI.
  for (let i = 0; i < live.length; i++) {
    const A = live[i];
    for (let j = i + 1; j < live.length; j++) {
      const B = live[j];
      const dx = A.x - B.x, dy = A.y - B.y, dz = A.z - B.z;
      const dvx = A.vx - B.vx, dvy = A.vy - B.vy, dvz = A.vz - B.vz;
      const a = dvx * dvx + dvy * dvy + dvz * dvz;
      const bq = 2 * (dx * dvx + dy * dvy + dz * dvz);
      const c = dx * dx + dy * dy + dz * dz - R2 * R2;
      if (c < 0) {
        // Overlapping: resolve now along line of centers.
        const d = norm(dx, dy, dz);
        best = { t: 0, kind: 'bb', a: A.id, b: B.id, nx: d > 1e-9 ? dx / d : 1, ny: d > 1e-9 ? dy / d : 0, nz: d > 1e-9 ? dz / d : 0 };
        continue;
      }
      if (a < 1e-12 || bq >= 0) continue; // stationary or separating
      const disc = bq * bq - 4 * a * c;
      if (disc < 0) continue;
      const t = (-bq - Math.sqrt(disc)) / (2 * a);
      if (t >= 0 && t <= dt && (!best || t < best.t)) {
        const nx = (dx + dvx * t) / R2, ny = (dy + dvy * t) / R2;
        best = { t, kind: 'bb', a: A.id, b: B.id, nx, ny, nz: (dz + dvz * t) / R2 };
      }
    }
  }
  // Cushion swept TOI (axis-aligned segments, line inset by R).
  for (const A of live) {
    if ((A.z > 0 || A.vz !== 0) && A.z >= 0) {
      const t = (A.vz + Math.sqrt(A.vz * A.vz + 2 * G * Math.max(0, A.z))) / G;
      if (t >= 0 && t <= dt && hole(A.x+A.vx*t,A.y+A.vy*t) === null && (!best || t < best.t)) best = { t, kind: 'floor', a: A.id, b: -1, nx: 0, ny: 0 };
    }
    if (A.z < -BALL_R) continue;
    for (const cu of CUSHIONS) {
      const horiz = cu.y1 === cu.y2;
      if (horiz) {
        if (A.vy === 0) continue;
        const lineY = cu.y1;
        const target = lineY === 0 ? BALL_R : TABLE_H - BALL_R;
        if ((lineY === 0 && A.vy >= 0) || (lineY === TABLE_H && A.vy <= 0)) continue;
        // Ball-ball separation can push a rail-frozen ball slightly past the
        // inset contact plane. Recover grounded overlaps only: airborne balls
        // may already have legitimately cleared the nose on an earlier step.
        const crossing = (target - A.y) / A.vy;
        if (crossing < 0 && (A.z > 1e-9 || A.vz !== 0)) continue;
        const t = Math.max(0, crossing);
        if (A.y < 0 || A.y > TABLE_H || t > dt || (best && t >= best.t)) continue;
        const cx = A.x + A.vx * t;
        const lo = Math.min(cu.x1, cu.x2) - 1e-6, hi = Math.max(cu.x1, cu.x2) + 1e-6;
        if (cx < lo || cx > hi || A.z + A.vz * t - 0.5 * G * t * t > 0.05) continue;
        best = { t, kind: 'rail', a: A.id, b: -1, nx: 0, ny: lineY === 0 ? 1 : -1 };
      } else {
        if (A.vx === 0) continue;
        const lineX = cu.x1;
        const target = lineX === 0 ? BALL_R : TABLE_W - BALL_R;
        if ((lineX === 0 && A.vx >= 0) || (lineX === TABLE_W && A.vx <= 0)) continue;
        const crossing = (target - A.x) / A.vx;
        if (crossing < 0 && (A.z > 1e-9 || A.vz !== 0)) continue;
        const t = Math.max(0, crossing);
        if (A.x < 0 || A.x > TABLE_W || t > dt || (best && t >= best.t)) continue;
        const cy = A.y + A.vy * t;
        const lo = Math.min(cu.y1, cu.y2) - 1e-6, hi = Math.max(cu.y1, cu.y2) + 1e-6;
        if (cy < lo || cy > hi || A.z + A.vz * t - 0.5 * G * t * t > 0.05) continue;
        best = { t, kind: 'rail', a: A.id, b: -1, nx: lineX === 0 ? 1 : -1, ny: 0 };
      }
    }
    // Jaw bumpers as static circles.
    for (const j of JAWS) {
      if (A.z > 0.05 && A.vz >= 0) continue;
      const dx = A.x - j.x, dy = A.y - j.y;
      const rr = BALL_R + j.r;
      const a = A.vx * A.vx + A.vy * A.vy;
      if (a < 1e-12) continue;
      const bq = 2 * (dx * A.vx + dy * A.vy);
      const c = dx * dx + dy * dy - rr * rr;
      if (c < 0) {
        if (A.z > 0.05) continue;
        if (bq >= 0) continue; // already leaving the jaw
        const d = norm(dx, dy) || 1e-9;
        if (!best || 0 < best.t) best = { t: 0, kind: 'jaw', a: A.id, b: -1, nx: dx / d, ny: dy / d };
        continue;
      }
      if (bq >= 0) continue;
      const disc = bq * bq - 4 * a * c;
      if (disc < 0) continue;
      const t = (-bq - Math.sqrt(disc)) / (2 * a);
      if (t >= 0 && t <= dt && (!best || t < best.t)) {
        const nx = (dx + A.vx * t) / rr, ny = (dy + A.vy * t) / rr;
        if (A.z + A.vz * t - 0.5 * G * t * t <= 0.05) best = { t, kind: 'jaw', a: A.id, b: -1, nx, ny };
      }
    }
  }
  return best;
}

function resolveBallBall(A: Ball, B: Ball, nx: number, ny: number, ev: ShotEvents, cueId: number, nz = 0): void {
  const tx = -ny, ty = nx;
  const vn = (A.vx - B.vx) * nx + (A.vy - B.vy) * ny + (A.vz - B.vz) * nz;
  // Contact-point tangential relative velocity (includes sidespin coupling).
  const vt = (A.vx - B.vx) * tx + (A.vy - B.vy) * ty + BALL_R * (A.wz + B.wz);
  if (vn < 0) {
    const jn = (-(1 + E_BALL) * vn) / 2; // per-unit-mass (equal masses)
    A.vx += jn * nx; A.vy += jn * ny; A.vz += jn * nz;
    B.vx -= jn * nx; B.vy -= jn * ny; B.vz -= jn * nz;
    // Coulomb-clamped tangential impulse with speed-dependent throw friction.
    let jt = -vt / 2;
    const maxJ = throwMu(vn) * jn;
    jt = Math.max(-maxJ, Math.min(maxJ, jt));
    A.vx += jt * tx; A.vy += jt * ty;
    B.vx -= jt * tx; B.vy -= jt * ty;
    const dwz = (2.5 * jt) / BALL_R; // dw = R*J/I with J = jt*m
    A.wz += dwz;
    B.wz += dwz;
    A.asleep = B.asleep = false;
    if (ev.firstContact === null && (A.id === cueId || B.id === cueId)) {
      const other = A.id === cueId ? B : A;
      ev.firstContact = other.n;
      ev.firstContactX = other.x;
    }
  }
  // Repair penetration even when an impact impulse was just applied. This
  // changes positions only, so resting contacts cannot gain kinetic energy.
  const distance = norm(A.x - B.x, A.y - B.y, A.z - B.z);
  if (distance < BALL_R * 2) {
    const push = (BALL_R * 2 - distance) / 2 + 1e-8;
    A.x += nx * push; A.y += ny * push; A.z = Math.max(0, A.z + nz * push);
    B.x -= nx * push; B.y -= ny * push; B.z = Math.max(0, B.z - nz * push);
  }
}

function resolveRail(A: Ball, nx: number, ny: number, ev: ShotEvents, contactMade: { v: boolean }): void {
  const vn = A.vx * nx + A.vy * ny;
  if (vn >= 0) return;
  const tx = -ny, ty = nx;
  // Contact offset from center to rail point ~ -n*R; rotational velocity (w x r).
  const rx = -nx * BALL_R, ry = -ny * BALL_R;
  const vrx = -A.wz * ry;
  const vry = A.wz * rx;
  const vtRel = (A.vx * tx + A.vy * ty) + (vrx * tx + vry * ty);
  // Cushion COR along the contact normal; Coulomb friction tangentially.
  // The ball re-enters the skid regime after, which costs roughly half speed.
  const jn = -(1 + E_CUSH_N) * vn;
  A.vx += jn * nx; A.vy += jn * ny;
  // Full stick desired, Coulomb-clamped (rails kill slip, keep most roll).
  let jt = -vtRel;
  const maxJ = MU_CUSH * jn;
  jt = Math.max(-maxJ, Math.min(maxJ, jt));
  A.vx += jt * tx; A.vy += jt * ty;
  // Torque about center: dwz = (r x J)/I, J = jt*m*t.
  A.wz += (2.5 * (rx * (jt * ty) - ry * (jt * tx))) / (BALL_R * BALL_R);
  A.asleep = false;
  if (contactMade.v || ev.firstContact !== null || ev.potted.length > 0) {
    ev.railAfterContact = true;
    if (A.n !== null && !(ev.objectRails ?? []).includes(A.n)) (ev.objectRails ??= []).push(A.n);
  }
}


function hole(x:number,y:number):number|null {
  for (const [i,p] of POCKETS.entries()) if ((x-p.x)**2+(y-p.y)**2 < p.r*p.r) return i;
  return null;
}

export function step(balls:Ball[],dt:number,ev:ShotEvents,cueId:number,contactMade:{v:boolean}):void {
  const near=balls.filter(b=>!b.potted&&POCKETS.some(p=>norm(b.x-p.x,b.y-p.y)<p.r+BALL_R+norm(b.vx,b.vy)*dt));
  const count=near.length?Math.max(1,Math.ceil(dt*1920),Math.ceil(Math.max(...near.map(b=>norm(b.vx,b.vy,b.vz)*dt/.001)))):1;
  for(let i=0;i<count;i++) stepSub(balls,dt/count,ev,cueId,contactMade);
}

function stepSub(balls: Ball[], dt: number, ev: ShotEvents, cueId: number, contactMade: { v: boolean }): void {
  if (ev.firstContact !== null || ev.potted.length > 0) contactMade.v = true;
  for (const b of balls) {
    if (b.potted || b.asleep) continue;
    if (Math.abs(b.z) <= 1e-9 && b.vz === 0 && hole(b.x,b.y) === null) friction(b, dt);
  }
  let remaining = dt;
  for (let iter = 0; iter < 64 && remaining > 1e-9; iter++) {
    const c = earliestContact(balls, remaining);
    if (!c) break;
    const adv = Math.min(c.t, remaining);
    for (const b of balls) {
      if (b.potted || b.asleep) continue;
      advance(b, adv, ev, cueId);
    }
    remaining -= adv;
    const byId = (id: number) => balls.find((b) => b.id === id)!;
    if (c.kind === 'bb') resolveBallBall(byId(c.a), byId(c.b), c.nx, c.ny, ev, cueId, c.nz ?? 0);
    else if (c.kind === 'floor') land(byId(c.a));
    else {
      const ball = byId(c.a);
      if (c.kind === 'rail') {
        if (c.nx > 0) ball.x = Math.max(ball.x, BALL_R);
        else if (c.nx < 0) ball.x = Math.min(ball.x, TABLE_W - BALL_R);
        if (c.ny > 0) ball.y = Math.max(ball.y, BALL_R);
        else if (c.ny < 0) ball.y = Math.min(ball.y, TABLE_H - BALL_R);
      }
      resolveRail(ball, c.nx, c.ny, ev, contactMade);
    }
  }
  if (remaining > 1e-9) {
    for (const b of balls) {
      if (b.potted || b.asleep) continue;
      advance(b, remaining, ev, cueId);
      if (b.z < 0 && hole(b.x,b.y) === null) land(b);
    }
  }
  for (const b of balls) {
    if (b.potted) continue;
    const pocket = hole(b.x,b.y);
    // Geometric termination: https://ekiefl.github.io/2020/12/20/pooltool-alg/
    const captured = pocket !== null && b.z <= 1e-9 && b.vz <= 0;
    if (captured) {
      b.potted = b.asleep = true;
      b.z = b.vz = b.vx = b.vy = b.wx = b.wy = b.wz = 0;
      if (b.n !== null) { ev.potted.push(b.n); (ev.pockets ??= []).push({n:b.n,pocket: pocket!}); }
      else ev.cuePotted = true;
    }
    if (!captured && (b.x < -0.12 || b.x > TABLE_W + 0.12 || b.y < -0.12 || b.y > TABLE_H + 0.12)) {
      // Escaped through a gap edge: off the table (foul, stays down).
      b.potted = true;
      b.asleep = true;
      b.z = b.vz = b.vx = b.vy = b.wx = b.wy = b.wz = 0;
      ev.offTable.push(b.n);
      if (b.n === null) ev.cuePotted = true;
      continue;
    }
    // Pure sidespin cannot move an isolated resting ball; do not hold the turn for its decay.
    if (!b.potted && Math.abs(b.z) <= 1e-9 && hole(b.x,b.y) === null && b.vz === 0 && norm(b.vx, b.vy) < SLEEP_V && norm(b.wx, b.wy) < SLEEP_W) {
      b.z = b.vz = b.vx = b.vy = b.wx = b.wy = b.wz = 0;
      b.asleep = true;
    }
  }
}

export function allAsleep(balls: Ball[]): boolean {
  return balls.every((b) => b.potted || b.asleep);
}

/** Run the shot to rest. Mutates balls. Returns events for rules. */
export function simulateShot(balls: Ball[], cueId: number, maxSim = 45, events?: ShotEvents): ShotEvents {
  const ev: ShotEvents = events ?? { firstContact: null, potted: [], offTable: [], railAfterContact: false, cuePotted: false };
  const contactMade = { v: false };
  let t = 0;
  while (t < maxSim && !allAsleep(balls)) {
    step(balls, DT, ev, cueId, contactMade);
    t += DT;
  }
  return ev;
}

/** State hash for determinism tests (FNV over quantized floats). */
export function hashState(balls: Ball[]): string {
  let h = 0x811c9dc5;
  for (const b of [...balls].sort((a, z) => a.id - z.id)) {
    for (const v of [b.x, b.y, b.z, b.vx, b.vy, b.vz, b.wx, b.wy, b.wz]) {
      const q = Math.round(v * 1e9);
      h ^= q & 0xffffffff;
      h = Math.imul(h, 0x01000193) >>> 0;
    }
    h ^= b.potted ? 1 : 0;
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return (h >>> 0).toString(16);
}
